<template>
	<div class="module-card" :class="{ 'module-card--draft': module.is_draft }">
		<!-- Drag Handle -->
		<div class="module-card__drag-handle" title="Перетащите для изменения порядка">
			<svg class="module-card__icon module-card__icon--drag" viewBox="0 0 24 24" fill="none" aria-hidden="true">
				<circle cx="8" cy="6" r="1.5" fill="currentColor" />
				<circle cx="16" cy="6" r="1.5" fill="currentColor" />
				<circle cx="8" cy="12" r="1.5" fill="currentColor" />
				<circle cx="16" cy="12" r="1.5" fill="currentColor" />
				<circle cx="8" cy="18" r="1.5" fill="currentColor" />
				<circle cx="16" cy="18" r="1.5" fill="currentColor" />
			</svg>
		</div>

		<!-- Content -->
		<div class="module-card__content">
			<!-- Header -->
			<div class="module-card__header">
				<div class="module-card__info">
					<h4 class="module-card__title">{{ module.title }}</h4>
					<p v-if="module.description" class="module-card__description">
						{{ module.description }}
					</p>
				</div>

				<!-- Status Badge -->
				<div class="module-card__badges">
					<span v-if="module.is_draft" class="module-card__badge module-card__badge--draft">
						Черновик
					</span>
					<span v-else-if="module.is_published" class="module-card__badge module-card__badge--published">
						Опубликован
					</span>
					<span v-else class="module-card__badge module-card__badge--unpublished">
						Не опубликован
					</span>
				</div>
			</div>

			<!-- Meta Info -->
			<div class="module-card__meta">
				<span class="module-card__meta-item">
					<svg class="module-card__icon module-card__meta-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
						<path d="M5 4.5C5 3.67 5.67 3 6.5 3H18a1 1 0 0 1 1 1v13.5a1 1 0 0 1-1 1H6.5A1.5 1.5 0 0 1 5 17V4.5Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
						<path d="M8 7h8M8 10.5h8M8 14h5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
					</svg>
					Порядок: {{ module.order_number }}
				</span>
				<span v-if="module.duration_minutes" class="module-card__meta-item">
					<svg class="module-card__icon module-card__meta-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
						<circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.8" />
						<path d="M12 8v4.4l2.8 1.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
					</svg>
					{{ module.duration_minutes }} мин
				</span>
			</div>

			<!-- Actions -->
			<div class="module-card__actions">
				<button
					class="module-card__action-btn module-card__action-btn--view"
					type="button"
					@click="$emit('view-lessons', module)"
					title="Просмотреть уроки"
				>
					<svg class="module-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
						<path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
						<circle cx="12" cy="12" r="2.8" stroke="currentColor" stroke-width="1.8" />
					</svg>
					Уроки
				</button>
				<button
					class="module-card__action-btn module-card__action-btn--edit"
					type="button"
					@click="$emit('edit', module)"
					title="Редактировать"
				>
					<svg class="module-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
						<path d="M4 20h4.2L18.5 9.7a2.1 2.1 0 1 0-3-3L5.2 17V20Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
						<path d="m13.8 8.4 2.8 2.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
					</svg>
					Редактировать
				</button>
				<button
					v-if="!module.is_published"
					class="module-card__action-btn module-card__action-btn--publish"
					type="button"
					@click="$emit('publish', module)"
					title="Опубликовать"
				>
					<svg class="module-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
						<path d="M20 12a8 8 0 1 1-4-6.9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
						<path d="m9 12.4 2.1 2.1 4.9-4.9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
					</svg>
					Опубликовать
				</button>
				<button
					v-else
					class="module-card__action-btn module-card__action-btn--unpublish"
					type="button"
					@click="$emit('unpublish', module)"
					title="Снять с публикации"
				>
					<svg class="module-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
						<circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.8" />
						<path d="M9.2 9.2 14.8 14.8M14.8 9.2 9.2 14.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
					</svg>
					Снять
				</button>
				<button
					class="module-card__action-btn module-card__action-btn--delete"
					type="button"
					@click="handleDelete"
					title="Удалить"
				>
					<svg class="module-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
						<path d="M4 7h16M9 3h6M7 7l1 13h8l1-13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
						<path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
					</svg>
					Удалить
				</button>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ModuleCard',
	props: {
		module: {
			type: Object,
			required: true,
		},
	},
	methods: {
		handleDelete() {
			if (
				confirm(
					`Вы уверены, что хотите удалить модуль "${this.module.title}"? Это действие нельзя отменить.`
				)
			) {
				this.$emit('delete', this.module)
			}
		},
	},
}
</script>

<style scoped>
.module-card {
	display: flex;
	gap: 12px;
	background-color: #ffffff;
	border: 2px solid #e8e8e8;
	border-radius: 12px;
	padding: 20px;
	transition: all 0.3s ease;
}

.module-card:hover {
	border-color: #0095ec;
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.15);
}

.module-card--draft {
	background-color: #fffbf0;
	border-color: #ffc107;
}

/* Drag Handle */
.module-card__drag-handle {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	color: #b0b0b0;
	font-size: 20px;
	cursor: grab;
	user-select: none;
}

.module-card__drag-handle:active {
	cursor: grabbing;
}

/* Content */
.module-card__content {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 16px;
}

/* Header */
.module-card__header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 16px;
}

.module-card__info {
	flex: 1;
}

.module-card__title {
	color: #252641a4;
	font-family: 'Poppins', Helvetica;
	font-size: 18px;
	margin: 0 0 8px 0;
}

.module-card__description {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	line-height: 1.5;
	margin: 0;
}

/* Badges */
.module-card__badges {
	display: flex;
	gap: 8px;
}

.module-card__badge {
	padding: 4px 12px;
	border-radius: 12px;
	font-family: 'Poppins', Helvetica;
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
}

.module-card__badge--draft {
	background-color: #fff3cd;
	color: #856404;
}

.module-card__badge--published {
	background-color: #d4edda;
	color: #155724;
}

.module-card__badge--unpublished {
	background-color: #f8d7da;
	color: #721c24;
}

/* Meta */
.module-card__meta {
	display: flex;
	gap: 16px;
	flex-wrap: wrap;
}

.module-card__meta-item {
	display: flex;
	align-items: center;
	gap: 6px;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
}

.module-card__meta-icon {
	width: 16px;
	height: 16px;
	flex-shrink: 0;
}

.module-card__icon {
	width: 16px;
	height: 16px;
	flex-shrink: 0;
}

.module-card__icon--drag {
	width: 20px;
	height: 20px;
}

/* Actions */
.module-card__actions {
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
}

.module-card__action-btn {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 8px 16px;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	background-color: #ffffff;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.module-card__action-btn:hover {
	transform: translateY(-2px);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.module-card__action-btn--view:hover {
	border-color: #0095ec;
	color: #0095ec;
}

.module-card__action-btn--edit:hover {
	border-color: #ffc107;
	color: #ffc107;
}

.module-card__action-btn--publish:hover {
	border-color: #28a745;
	color: #28a745;
}

.module-card__action-btn--unpublish:hover {
	border-color: #dc3545;
	color: #dc3545;
}

.module-card__action-btn--delete:hover {
	border-color: #dc3545;
	color: #dc3545;
	background-color: #fff5f5;
}
</style>
